import { useEffect, useRef } from 'react';

/**
 * Cloudflare Turnstile 人机验证组件
 *
 * 显式渲染 widget；token 一次性（siteverify 消费后失效），
 * 失败重试时父组件通过递增 key 重挂载获取新 token
 *
 * 慢网络优化：
 * - refresh-expired: auto —— token 过期自动刷新，无需用户重新操作
 * - retry: auto —— 挑战执行超时/失败由 widget 自动重试
 * - timeout-callback —— 上报状态，父组件可提示“验证较慢，正在重试”
 */
declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      remove: (id: string) => void;
    };
  }
}

let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => {
        scriptPromise = null;
        reject(new Error('turnstile script load failed'));
      };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

export type TurnstileStatus = 'loading' | 'ready' | 'timeout';

interface TurnstileProps {
  siteKey: string;
  /** token 为空串表示未通过/已过期 */
  onToken: (token: string) => void;
  /** 脚本加载失败回调（网络拦截等场景提示用户） */
  onLoadError?: () => void;
  /** widget 状态变化：loading 初始化中 / ready 已通过 / timeout 挑战执行超时（自动重试中） */
  onStatus?: (status: TurnstileStatus) => void;
}

export default function Turnstile({ siteKey, onToken, onLoadError, onStatus }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;
  const onStatusRef = useRef(onStatus);
  onStatusRef.current = onStatus;

  useEffect(() => {
    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token: string) => {
            onTokenRef.current(token);
            onStatusRef.current?.('ready');
          },
          'expired-callback': () => onTokenRef.current(''),
          'error-callback': () => onTokenRef.current(''),
          'timeout-callback': () => onStatusRef.current?.('timeout'),
          'refresh-expired': 'auto',
          retry: 'auto',
        });
      })
      .catch(() => {
        if (!cancelled) onLoadError?.();
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  return <div ref={containerRef} style={{ marginBottom: '1rem' }} />;
}
